import { subcategoryDisplayLabel, type LumaSize, type LumaSubcategory } from './types';

/**
 * Validates the current configurator selections and submits the add-to-cart
 * request via a standard WooCommerce HTML form POST.
 *
 * Using a form POST (rather than a REST API call) is the reliable WooCommerce
 * pattern for custom cart item metadata: the `woocommerce_add_cart_item_data`
 * PHP filter reads from $_POST and injects the data into the cart item before
 * WC persists it to the session.
 */
export class CartSubmitter {

	private addToCartNonce: string;

	constructor(addToCartNonce: string) {
		this.addToCartNonce = addToCartNonce;
	}

	/**
	 * Validate that all required selections are present. Returns an error message
	 * string if validation fails, or null if everything is complete.
	 */
	validate(
		productId: number,
		subcat: LumaSubcategory | null,
		size: LumaSize | null,
		selectedOptions: Record<string, number>,
	): string | null {
		if (!productId) return 'No product selected.';
		if (!subcat)    return 'Please select a product type.';
		if (!size)      return 'Please select a size.';

		for (const group of subcat.optionGroups) {
			if (!selectedOptions[group.groupName]) {
				return `Please select a ${group.groupName}.`;
			}
		}

		return null;
	}

	/**
	 * Submit the configurator selections as one or more WC add-to-cart form POSTs.
	 *
	 * Single-image products: one standard form POST → WooCommerce redirects to cart.
	 *
	 * Multi-panel products (productionImageCount > 1): each panel is a separate cart
	 * item because it will become a separate Lumaprints order at fulfilment time.
	 * Panels 0 … N-2 are submitted via fetch() so the browser stays on the page.
	 * The final panel is submitted as a real form POST, triggering WooCommerce's
	 * normal cart redirect and carrying all items added in the previous fetches.
	 *
	 * Resolves after background panel POSTs complete (multi-panel); the final
	 * form submit usually unloads the page immediately after.
	 */
	async submit(params: {
		productId: number;
		subcat: LumaSubcategory;
		size: LumaSize;
		selectedOptions: Record<string, number>;
		basePrice: number;
		displayedPrice: number;
		productionImageCount: number;
	}): Promise<void> {
		const { productId, subcat, size, selectedOptions, basePrice, displayedPrice, productionImageCount } = params;

		const optionIds   = Object.values(selectedOptions);
		const displayMeta = this.buildDisplayMeta(subcat, size, selectedOptions);
		const panelCount  = productionImageCount > 1 ? productionImageCount : 1;

		const baseFields: Record<string, string> = {
			'add-to-cart':            String(productId),
			'quantity':               '1',
			'luma_nonce':             this.addToCartNonce,

			// API meta — used for Lumaprints order sync and shipping.
			'_luma_subcategory_id':   String(subcat.subcategoryId),
			'_luma_width':            String(size.width),
			'_luma_height':           String(size.height),
			'_luma_base_price':       String(basePrice),
			'_luma_displayed_price':  String(displayedPrice),
			'_luma_option_ids':       JSON.stringify(optionIds),

			// Display meta — shown to customer in cart, checkout, and order emails.
			'luma_display_product_type': displayMeta.productType,
			'luma_display_size':         displayMeta.size,
		};

		if (displayMeta.border)  baseFields['luma_display_border']  = displayMeta.border;
		if (displayMeta.finish)  baseFields['luma_display_finish']  = displayMeta.finish;
		if (displayMeta.frame)   baseFields['luma_display_frame']   = displayMeta.frame;
		if (displayMeta.mat)     baseFields['luma_display_mat']     = displayMeta.mat;

		if (panelCount === 1) {
			// Single image — simple form submit (no panel meta needed).
			baseFields['_luma_panel_index'] = '0';
			await new Promise<void>((resolve) => {
				requestAnimationFrame(() => resolve());
			});
			this.submitForm(baseFields);
			return;
		}

		// Multi-panel: submit panels 0…N-2 via fetch, then do a real form submit
		// for the last panel so WooCommerce handles the cart redirect normally.
		await this.submitPanelsSequentially(baseFields, panelCount);
	}

	/**
	 * Submit panels 0…N-2 as background fetch requests, then do a full form POST
	 * for the last panel so WooCommerce redirects to the cart page.
	 */
	private async submitPanelsSequentially(
		baseFields: Record<string, string>,
		panelCount: number,
	): Promise<void> {
		const url = window.location.href;

		for (let i = 0; i < panelCount - 1; i++) {
			const body = new URLSearchParams({
				...baseFields,
				'_luma_panel_index': String(i),
				'_luma_panel_total': String(panelCount),
			});

			try {
				await fetch(url, {
					method:      'POST',
					headers:     { 'Content-Type': 'application/x-www-form-urlencoded' },
					body:        body.toString(),
					credentials: 'same-origin',
				});
			} catch {
				// Continue even if a single fetch fails; the add-to-cart will be
				// incomplete but the last panel's form submit will still run and
				// PHP validation will surface any missing items to the customer.
			}
		}

		// Final panel — real form POST so WC redirects to the cart page.
		const finalFields: Record<string, string> = {
			...baseFields,
			'_luma_panel_index': String(panelCount - 1),
			'_luma_panel_total': String(panelCount),
		};
		this.submitForm(finalFields);
	}

	/**
	 * Build a hidden-input form from the given fields and submit it, causing a
	 * full page navigation (WooCommerce will redirect to the cart page).
	 */
	private submitForm(fields: Record<string, string>): void {
		const form = document.createElement('form');
		form.method = 'POST';
		form.action = window.location.href;

		for (const [name, value] of Object.entries(fields)) {
			const input    = document.createElement('input');
			input.type     = 'hidden';
			input.name     = name;
			input.value    = value;
			form.appendChild(input);
		}

		document.body.appendChild(form);
		form.submit();
	}

	/**
	 * Build the display meta payload: human-readable labels for cart/order display.
	 */
	private buildDisplayMeta(
		subcat: LumaSubcategory,
		size: LumaSize,
		selectedOptions: Record<string, number>,
	): { productType: string; size: string; border?: string; finish?: string; frame?: string; mat?: string } {
		const meta: ReturnType<CartSubmitter['buildDisplayMeta']> = {
			productType: subcategoryDisplayLabel(subcat),
			size:        `${size.width} × ${size.height} in`,
		};

		const groupToField: Record<string, 'border' | 'finish' | 'frame' | 'mat'> = {
			'canvas border': 'border',
			'border':        'border',
			'finish':        'finish',
			'paper finish':  'finish',
			'paper type':    'finish',
			'frame color':   'frame',
			'frame style':   'frame',
			'frame':         'frame',
			'mat':           'mat',
			'mat option':    'mat',
		};

		for (const group of subcat.optionGroups) {
			const fieldKey = groupToField[group.groupName.toLowerCase()];
			const optionId = selectedOptions[group.groupName];

			if (!fieldKey || !optionId) continue;

			const item = group.items.find((i) => i.optionId === optionId);

			if (item) {
				meta[fieldKey] = item.optionName;
			}
		}

		if (meta.frame) {
			if (subcat.name.toLowerCase().includes('standard')) {
				meta.frame = `Standard Frame — ${meta.frame}`;
			} else if (subcat.name.toLowerCase().includes('premium')) {
				meta.frame = `Premium Frame — ${meta.frame}`;
			}
		}

		return meta;
	}
}
